import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'

import { fetchClientFlags, type ClientFlagsResponse } from '../api/flags'
import { useAuth } from '../contexts/AuthContext'
import { createNadDebug } from '../utils/debugLog'

// NAD trace for the client flag-read boundary (feature=feature-flags-client).
const flagsLog = createNadDebug('dw:feature-flags-client:client')

const EMPTY: ClientFlagsResponse = { flags: {} }
type ClientFlagSnapshot = {
  userId: string | undefined
  flags: Record<string, boolean | number | string>
}

export function clientFlagsQueryKey(userId: string | undefined): (string | undefined)[] {
  return ['flags', 'client', userId]
}

export function resolveClientFlagsSnapshot(
  currentFlags: Record<string, boolean | number | string> | undefined,
  lastGoodSnapshot: ClientFlagSnapshot | undefined,
  userId: string | undefined,
): Record<string, boolean | number | string> {
  if (currentFlags) return currentFlags
  if (lastGoodSnapshot !== undefined && lastGoodSnapshot.userId === userId) {
    return lastGoodSnapshot.flags
  }
  return EMPTY.flags
}

/**
 * Fetch + cache the browser flag snapshot (RFC-0856 I10).
 *
 * - Keyed on the user id so login/logout auto-refetches (the set is per-user
 *   once targeting lands; for v0 global flags it just re-reads).
 * - `refetchInterval` (not `staleTime` alone) is what actually bounds browser
 *   staleness — react-query `staleTime` only marks data stale, it doesn't poll.
 * - Gated on an authenticated user so it never fires an expected 401 during
 *   session restore / while logged out.
 * - Resilient: a fetch error never throws into render. Refetch failures keep
 *   the last good snapshot so transient API errors do not disable enabled UI.
 */
export function useClientFlags(): { flags: Record<string, boolean | number | string>; isLoading: boolean } {
  const { user, isLoading: authLoading } = useAuth()
  const userId = user?.id
  const lastGoodFlagsRef = useRef<ClientFlagSnapshot | undefined>(undefined)

  const query = useQuery<ClientFlagsResponse>({
    queryKey: clientFlagsQueryKey(userId),
    queryFn: async () => {
      try {
        const res = await fetchClientFlags()
        flagsLog({
          event: 'feature-flags-client:fetch',
          feature: 'feature-flags-client',
          concern: 'client',
          count: Object.keys(res.flags).length,
        })
        return res
      } catch (error) {
        flagsLog({
          event: 'feature-flags-client:fetch_failed',
          feature: 'feature-flags-client',
          concern: 'client',
        })
        throw error
      }
    },
    enabled: !authLoading && user !== null,
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    if (query.data) {
      lastGoodFlagsRef.current = { userId, flags: query.data.flags }
    }
  }, [query.data, userId])

  return {
    flags: resolveClientFlagsSnapshot(query.data?.flags, lastGoodFlagsRef.current, userId),
    isLoading: query.isLoading && !query.data,
  }
}
